import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';

export default function LoginCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check URL for error parameters from OAuth
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    const errorDescription = params.get('error_description');

    if (errorParam) {
      setError(errorDescription || errorParam);
      return;
    }

    // If no code or error, redirect to login
    const code = params.get('code');
    if (!code && !errorParam) {
      navigate('/login', { replace: true });
    }

    // Amplify handles the code exchange automatically via Hub
    // useAuth will update when signedIn event fires
  }, [navigate]);

  // Redirect to dashboard once user is authenticated
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-mk-pink-50 to-white px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-red-500 text-6xl mb-4">!</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('login.errors.authFailed')}
            </h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link
              to="/login"
              className="inline-block py-3 px-6 bg-mk-pink-600 text-white rounded-lg font-semibold hover:bg-mk-pink-700 transition-colors"
            >
              {t('login.tryAgain')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mk-pink-600 mx-auto mb-4"></div>
        <p className="text-gray-600">{t('login.signingIn')}</p>
      </div>
    </div>
  );
}
